import { FC, useState, useEffect, useCallback } from 'react'
import { SellerConfigItem } from '../types'
import {
  getSellerConfig,
  createSellerConfig,
  updateSellerConfig,
  deleteSellerConfig,
  getSellerPanelLink,
} from '../services'
import { PrintQRCodeModal } from './PrintQRCodeModal'
import Swal from 'sweetalert2'

interface ConfigTabProps {
  pluginspaceId: number
  onConfigChanged?: () => void
}

export const ConfigTab: FC<ConfigTabProps> = ({ pluginspaceId, onConfigChanged }) => {
  const getDefaultPanelLink = useCallback(() => {
    const appBasePath = process.env.PUBLIC_URL || ''
    const normalizedBasePath = appBasePath.endsWith('/')
      ? appBasePath.slice(0, -1)
      : appBasePath

    return `${window.location.origin}${normalizedBasePath}/seller/queue-painel`
  }, [])

  const getEffectivePanelLink = useCallback(
    (linkPainel?: string | null) => {
      const normalizedLinkPainel = String(linkPainel || '').trim()
      return normalizedLinkPainel.length > 0
        ? normalizedLinkPainel
        : getDefaultPanelLink()
    },
    [getDefaultPanelLink],
  )

  const [showHelp, setShowHelp] = useState(false)
  const [config, setConfig] = useState<SellerConfigItem>({
    pluginspaceId,
    sendMailQueue: false,
    sendPush: false,
    sendWhatsapp: false,
    sendSMS: false,
    linkPainel: getDefaultPanelLink(),
    coupledQueueView: false,
    multiCustomerEnabled: false,
    maxCustomersPerSession: 1,
  })

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [hasExistingConfig, setHasExistingConfig] = useState(false)
  const [showQRModal, setShowQRModal] = useState(false)

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true)
      console.log('[ConfigTab] Loading config...')
      const existingConfig = await getSellerConfig()
      const defaultLinkPainel = getDefaultPanelLink()
      
      if (existingConfig) {
        console.log('[ConfigTab] Found existing config:', existingConfig)

        const normalizedLinkPainel = getEffectivePanelLink(existingConfig.linkPainel)
        const normalizedConfig: SellerConfigItem = {
          ...existingConfig,
          pluginspaceId: existingConfig.pluginspaceId || pluginspaceId,
          sendMailQueue: Boolean(existingConfig.sendMailQueue),
          sendPush: Boolean(existingConfig.sendPush),
          sendWhatsapp: Boolean(existingConfig.sendWhatsapp),
          sendSMS: Boolean(existingConfig.sendSMS),
          coupledQueueView: Boolean(existingConfig.coupledQueueView),
          multiCustomerEnabled: Boolean(existingConfig.multiCustomerEnabled),
          maxCustomersPerSession: Number(existingConfig.maxCustomersPerSession || 1),
          linkPainel: normalizedLinkPainel,
        }

        setConfig(normalizedConfig)
        setHasExistingConfig(true)

        const originalLinkPainel = String(existingConfig.linkPainel || '').trim()
        if (!originalLinkPainel) {
          await updateSellerConfig({
            pluginspaceId: normalizedConfig.pluginspaceId,
            sendMailQueue: normalizedConfig.sendMailQueue,
            sendPush: normalizedConfig.sendPush,
            sendWhatsapp: normalizedConfig.sendWhatsapp,
            sendSMS: normalizedConfig.sendSMS,
            linkPainel: normalizedLinkPainel,
            coupledQueueView: normalizedConfig.coupledQueueView,
            multiCustomerEnabled: normalizedConfig.multiCustomerEnabled,
            maxCustomersPerSession: normalizedConfig.maxCustomersPerSession,
          })
        }
      } else {
        console.log('[ConfigTab] No existing config found, using defaults')
        const defaultConfig: SellerConfigItem = {
          pluginspaceId,
          sendMailQueue: false,
          sendPush: false,
          sendWhatsapp: false,
          sendSMS: false,
          linkPainel: defaultLinkPainel,
          coupledQueueView: false,
          multiCustomerEnabled: false,
          maxCustomersPerSession: 1,
        }

        setConfig(defaultConfig)

        const createdConfig = await createSellerConfig(defaultConfig)
        if (createdConfig) {
          setConfig({
            ...defaultConfig,
            ...createdConfig,
            linkPainel: getEffectivePanelLink(createdConfig.linkPainel),
          })
          setHasExistingConfig(true)
        } else {
          setHasExistingConfig(false)
        }
      }
    } catch (error) {
      console.error('[ConfigTab] Error loading config:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar configurações',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }, [getDefaultPanelLink, getEffectivePanelLink, pluginspaceId])

  // Carregar configuração ao montar o componente
  useEffect(() => {
    loadConfig()
  }, [loadConfig])

  const handleCheckboxChange = (field: keyof Pick<SellerConfigItem, 'sendMailQueue' | 'sendPush' | 'sendWhatsapp' | 'sendSMS'>) => {
    setConfig((prev) => ({
      ...prev,
      [field]: !prev[field],
    }))
  }

  const handleLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfig((prev) => ({
      ...prev,
      linkPainel: e.target.value || null,
    }))
  }

  const handleUseInternalPanelLink = async () => {
    const appBasePath = process.env.PUBLIC_URL || ''
    const normalizedBasePath = appBasePath.endsWith('/')
      ? appBasePath.slice(0, -1)
      : appBasePath
    try {
      const internalPanelLink = await getSellerPanelLink(
        `${window.location.origin}${normalizedBasePath}`
      )

      setConfig((prev) => ({
        ...prev,
        linkPainel: internalPanelLink,
      }))
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao gerar link seguro',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    }
  }

  const handleCoupledQueueViewChange = async () => {
    if (saving) return

    const nextCoupledValue = !config.coupledQueueView
    const previousConfig = config

    setConfig((prev) => ({
      ...prev,
      coupledQueueView: nextCoupledValue,
    }))

    try {
      setSaving(true)

      const dataToSave = {
        pluginspaceId: config.pluginspaceId,
        sendMailQueue: config.sendMailQueue,
        sendPush: config.sendPush,
        sendWhatsapp: config.sendWhatsapp,
        sendSMS: config.sendSMS,
        linkPainel: getEffectivePanelLink(config.linkPainel),
        coupledQueueView: nextCoupledValue,
        multiCustomerEnabled: config.multiCustomerEnabled,
        maxCustomersPerSession: config.maxCustomersPerSession,
      }

      let result: SellerConfigItem
      if (hasExistingConfig) {
        result = await updateSellerConfig(dataToSave)
      } else {
        result = await createSellerConfig(dataToSave)
        setHasExistingConfig(true)
      }

      if (result) {
        setConfig((previous) => ({ ...previous, ...result }))
      }

      await onConfigChanged?.()
    } catch (error) {
      setConfig(previousConfig)
      Swal.fire({
        icon: 'error',
        title: 'Erro ao atualizar visão acoplada',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setSaving(false)
    }
  }

  const persistMultiCustomerConfig = async (
    multiCustomerEnabled: boolean,
    maxCustomersPerSession: number,
  ) => {
    if (saving) return

    const previousConfig = config
    const normalizedLimit = Math.min(10, Math.max(1, Math.trunc(maxCustomersPerSession) || 1))
    const nextConfig = {
      ...config,
      multiCustomerEnabled,
      maxCustomersPerSession: normalizedLimit,
    }

    setConfig(nextConfig)
    setSaving(true)

    try {
      const dataToSave = {
        pluginspaceId: nextConfig.pluginspaceId,
        sendMailQueue: nextConfig.sendMailQueue,
        sendPush: nextConfig.sendPush,
        sendWhatsapp: nextConfig.sendWhatsapp,
        sendSMS: nextConfig.sendSMS,
        linkPainel: getEffectivePanelLink(nextConfig.linkPainel),
        coupledQueueView: nextConfig.coupledQueueView,
        multiCustomerEnabled,
        maxCustomersPerSession: normalizedLimit,
      }

      const result = hasExistingConfig
        ? await updateSellerConfig(dataToSave)
        : await createSellerConfig(dataToSave)

      setHasExistingConfig(true)
      if (result) {
        setConfig((previous) => ({
          ...previous,
          ...result,
          multiCustomerEnabled: Boolean(
            result.multiCustomerEnabled ?? multiCustomerEnabled,
          ),
          maxCustomersPerSession: Number(
            result.maxCustomersPerSession ?? normalizedLimit,
          ),
        }))
      }
      await onConfigChanged?.()
    } catch (error) {
      setConfig(previousConfig)
      Swal.fire({
        icon: 'error',
        title: 'Erro ao salvar atendimento múltiplo',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const effectivePanelLink = getEffectivePanelLink(config.linkPainel)
      
      // Validação simples
      if (effectivePanelLink && !isValidUrl(effectivePanelLink)) {
        Swal.fire({
          icon: 'warning',
          title: 'URL inválida',
          text: 'Por favor, insira uma URL válida para o painel',
          confirmButtonText: 'OK',
        })
        setSaving(false)
        return
      }

      // Prepara os dados para enviar (sem id, created, updated)
      const dataToSave = {
        pluginspaceId: config.pluginspaceId,
        sendMailQueue: config.sendMailQueue,
        sendPush: config.sendPush,
        sendWhatsapp: config.sendWhatsapp,
        sendSMS: config.sendSMS,
        linkPainel: effectivePanelLink,
        coupledQueueView: config.coupledQueueView,
        multiCustomerEnabled: config.multiCustomerEnabled,
        maxCustomersPerSession: Math.min(
          10,
          Math.max(1, Math.trunc(Number(config.maxCustomersPerSession)) || 1),
        ),
      }

      console.log('[ConfigTab] Saving config:', dataToSave)

      let result: SellerConfigItem
      if (hasExistingConfig) {
        console.log('[ConfigTab] Updating existing config...')
        result = await updateSellerConfig(dataToSave)
      } else {
        console.log('[ConfigTab] Creating new config...')
        result = await createSellerConfig(dataToSave)
        setHasExistingConfig(true)
      }

      console.log('[ConfigTab] Save success, result:', result)
      
      // Atualiza o estado com resposta do servidor
      if (result) {
        setConfig((previous) => ({
          ...previous,
          ...result,
          multiCustomerEnabled: Boolean(
            result.multiCustomerEnabled ?? config.multiCustomerEnabled,
          ),
          maxCustomersPerSession: Number(
            result.maxCustomersPerSession ?? config.maxCustomersPerSession ?? 1,
          ),
        }))
      }

      onConfigChanged?.()

      Swal.fire({
        icon: 'success',
        title: 'Sucesso!',
        text: 'Configurações salvas com sucesso!',
        confirmButtonText: 'OK',
      })
    } catch (error) {
      console.error('[ConfigTab] Error saving config:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro ao salvar',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!hasExistingConfig) {
      Swal.fire({
        icon: 'info',
        title: 'Nenhuma configuração',
        text: 'Não há configurações para deletar',
        confirmButtonText: 'OK',
      })
      return
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'Confirmar exclusão',
      text: 'Tem certeza que deseja deletar todas as configurações?',
      showCancelButton: true,
      confirmButtonText: 'Sim, deletar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc3545',
    })

    if (result.isConfirmed) {
      try {
        setSaving(true)
        console.log('[ConfigTab] Deleting config...')
        await deleteSellerConfig()

        // Resetar formulário
        setConfig({
          pluginspaceId,
          sendMailQueue: false,
          sendPush: false,
          sendWhatsapp: false,
          sendSMS: false,
          linkPainel: getDefaultPanelLink(),
          coupledQueueView: false,
          multiCustomerEnabled: false,
          maxCustomersPerSession: 1,
        })
        setHasExistingConfig(false)

        onConfigChanged?.()

        Swal.fire({
          icon: 'success',
          title: 'Deletado!',
          text: 'Configurações deletadas com sucesso!',
          confirmButtonText: 'OK',
        })
      } catch (error) {
        console.error('[ConfigTab] Error deleting config:', error)
        Swal.fire({
          icon: 'error',
          title: 'Erro ao deletar',
          text: error instanceof Error ? error.message : 'Erro desconhecido',
          confirmButtonText: 'OK',
        })
      } finally {
        setSaving(false)
      }
    }
  }

  const isValidUrl = (urlString: string): boolean => {
    try {
      new URL(urlString)
      return true
    } catch {
      return false
    }
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ height: '300px' }}>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Carregando...</span>
        </div>
      </div>
    )
  }

  const effectivePanelLink = getEffectivePanelLink(config.linkPainel)

  return (
    <div className='container-fluid mt-4'>
      <div className='row'>
        <div className='col-md-8'>
          <div className='card'>
            <div className='card-header'>
              <h3 className='card-title'>Configurações de Notificação</h3>
            </div>
            <div className='card-body'>
              <div className='accordion mb-4'>
                <div className='accordion-item'>
                  <h2 className='accordion-header'>
                    <button
                      className={`accordion-button ${showHelp ? '' : 'collapsed'}`}
                      type='button'
                      onClick={() => setShowHelp((prev) => !prev)}
                      aria-expanded={showHelp}
                      style={{ backgroundColor: showHelp ? '#f0f6ff' : 'transparent' }}
                    >
                      <i className='fas fa-lightbulb me-2 text-warning'></i>
                      <strong>Saiba mais...</strong>
                      <span className='ms-2 text-muted'>(Configuração da Fila)</span>
                    </button>
                  </h2>
                  {showHelp && (
                    <div className='accordion-body bg-light-info p-4'>
                      <ul className='list-unstyled mb-0'>
                        <li className='mb-2 d-flex'>
                          <i className='fas fa-check-circle text-success me-3 mt-1' style={{ minWidth: '20px' }}></i>
                          <span className='text-muted'>
                            Os canais <strong>Email</strong>, <strong>WhatsApp</strong> e <strong>SMS</strong> são serviços ativados mediante contratação.
                          </span>
                        </li>
                        <li className='mb-2 d-flex'>
                          <i className='fas fa-check-circle text-success me-3 mt-1' style={{ minWidth: '20px' }}></i>
                          <span className='text-muted'>
                            Ao habilitar esses canais sem contratação ativa, os envios podem não ocorrer.
                          </span>
                        </li>
                        <li className='mb-2 d-flex'>
                          <i className='fas fa-check-circle text-success me-3 mt-1' style={{ minWidth: '20px' }}></i>
                          <span className='text-muted'>
                            O Botão <strong>Gerar QR Code</strong> cria um QR Code que direciona para o link do painel de fila, facilitando o acesso dos vendedores, apenas
                            é ativado quando o link do painel estiver preenchido.
                          </span>
                        </li>
                        <li className='d-flex'>
                          <i className='fas fa-check-circle text-success me-3 mt-1' style={{ minWidth: '20px' }}></i>
                          <span className='text-muted'>
                            A opção <strong>Visão acoplada da fila (Fila Atual + Em Atendimento)</strong> funciona igual na página <strong>/seller/queue</strong> aba <strong>Fila Atual</strong>: quando ativa, as duas visões aparecem juntas na mesma tela, em colunas.
                          </span>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Checkbox: Email */}
              <div className='mb-4'>
                <div className='form-check form-switch'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='sendMailQueue'
                    checked={config.sendMailQueue}
                    onChange={() => handleCheckboxChange('sendMailQueue')}
                    disabled={saving}
                  />
                  <label className='form-check-label' htmlFor='sendMailQueue'>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>
                      Enviar email para vendedores quando alterar fila
                    </span>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '5px', marginBottom: '0' }}>
                      Notifica vendedores por email sobre mudanças de posição na fila
                    </p>
                  </label>
                </div>
              </div>

              {/* Checkbox: Push */}
              <div className='mb-4'>
                <div className='form-check form-switch'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='sendPush'
                    checked={config.sendPush}
                    onChange={() => handleCheckboxChange('sendPush')}
                    disabled={saving}
                  />
                  <label className='form-check-label' htmlFor='sendPush'>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>
                      Enviar Push para vendedores quando alterar fila
                    </span>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '5px', marginBottom: '0' }}>
                      Notifica vendedores através de push notification sobre mudanças de posição
                    </p>
                  </label>
                </div>
              </div>

              <div className='mb-4 border-top pt-4'>
                <div className='form-check form-switch'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='multiCustomerEnabled'
                    checked={config.multiCustomerEnabled}
                    onChange={() =>
                      void persistMultiCustomerConfig(
                        !config.multiCustomerEnabled,
                        config.maxCustomersPerSession,
                      )
                    }
                    disabled={saving}
                  />
                  <label className='form-check-label' htmlFor='multiCustomerEnabled'>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>
                      Permitir atendimento de múltiplos clientes
                    </span>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '5px', marginBottom: '0' }}>
                      Mantém um atendimento do vendedor e registra o resultado de cada cliente separadamente.
                    </p>
                  </label>
                </div>
                <div className='mt-3' style={{ maxWidth: '240px' }}>
                  <label className='form-label' htmlFor='maxCustomersPerSession'>
                    Limite de clientes simultâneos
                  </label>
                  <input
                    className='form-control'
                    type='number'
                    id='maxCustomersPerSession'
                    min={1}
                    max={10}
                    value={config.maxCustomersPerSession}
                    onChange={(event) => {
                      const value = Math.min(10, Math.max(1, Number(event.target.value) || 1))
                      setConfig((prev) => ({ ...prev, maxCustomersPerSession: value }))
                    }}
                    onBlur={() =>
                      void persistMultiCustomerConfig(
                        config.multiCustomerEnabled,
                        config.maxCustomersPerSession,
                      )
                    }
                    disabled={saving || !config.multiCustomerEnabled}
                  />
                </div>
              </div>

              {/* Checkbox: WhatsApp */}
              <div className='mb-4'>
                <div className='form-check form-switch'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='sendWhatsapp'
                    checked={config.sendWhatsapp}
                    onChange={() => handleCheckboxChange('sendWhatsapp')}
                    disabled={saving}
                  />
                  <label className='form-check-label' htmlFor='sendWhatsapp'>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>
                      Enviar WhatsApp para vendedores quando alterar fila
                    </span>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '5px', marginBottom: '0' }}>
                      Notifica vendedores via WhatsApp sobre mudanças de posição na fila
                    </p>
                  </label>
                </div>
              </div>

              {/* Checkbox: SMS */}
              <div className='mb-4'>
                <div className='form-check form-switch'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='sendSMS'
                    checked={config.sendSMS}
                    onChange={() => handleCheckboxChange('sendSMS')}
                    disabled={saving}
                  />
                  <label className='form-check-label' htmlFor='sendSMS'>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>
                      Enviar SMS para vendedores quando alterar fila
                    </span>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '5px', marginBottom: '0' }}>
                      Notifica vendedores via SMS sobre mudanças de posição na fila
                    </p>
                  </label>
                </div>
              </div>

              {/* Input: Link do Painel */}
              {/* <div className='mb-4'>
                <label htmlFor='linkPainel' className='form-label' style={{ fontSize: '16px', fontWeight: '500' }}>
                  Link do Painel de Fila
                </label>
                <input
                  type='url'
                  className='form-control'
                  id='linkPainel'
                  placeholder='https://exemplo.com/painel-fila'
                  value={config.linkPainel || ''}
                  onChange={handleLinkChange}
                  disabled={saving}
                  style={{ fontSize: '14px' }}
                />
                <small className='form-text text-muted d-block mt-2'>
                  URL para o painel de acompanhamento da fila que será compartilhada com os vendedores
                </small>
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary mt-3'
                  onClick={handleUseInternalPanelLink}
                  disabled={saving}
                >
                  <i className='fas fa-link me-2'></i>
                  Usar link interno do painel
                </button>
              </div> */}

              <div className='mb-4'>
                <div className='form-check form-switch'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='coupledQueueView'
                    checked={config.coupledQueueView}
                    onChange={handleCoupledQueueViewChange}
                    disabled={saving}
                  />
                  <label className='form-check-label' htmlFor='coupledQueueView'>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>
                      Visão acoplada da fila (Fila Atual + Em Atendimento)
                    </span>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '5px', marginBottom: '0' }}>
                      Quando ativa, exibe as duas visões na mesma tela em colunas.
                    </p>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Card: Link da Fila de Espera com Ações */}
          <div className='card mt-4'>
            <div className='card-header'>
              <h3 className='card-title'>Link da Fila de Espera</h3>
            </div>
            <div className='card-body'>
              <div className='mb-3 p-3 bg-light rounded' style={{ wordBreak: 'break-all', fontSize: '14px' }}>
                {effectivePanelLink}
              </div>
              <small className='form-text text-muted d-block mt-2'>
                URL para o painel de acompanhamento da fila que será compartilhada com os vendedores
              </small>
              <div className='d-flex gap-2'>
                <button
                  className='btn btn-sm btn-outline-primary'
                  onClick={() => {
                    navigator.clipboard.writeText(effectivePanelLink)
                    Swal.fire({
                      icon: 'success',
                      title: 'Copiado!',
                      text: 'Link copiado para a área de transferência',
                      timer: 2000,
                      showConfirmButton: false,
                    })
                  }}
                  style={{ fontSize: '13px', fontWeight: '500' }}
                >
                  <i className='fas fa-share-alt me-1'></i>
                  Compartilhar
                </button>
                <button
                  className='btn btn-sm btn-outline-info'
                  onClick={() => setShowQRModal(true)}
                  style={{ fontSize: '13px', fontWeight: '500' }}
                >
                  <i className='fas fa-qrcode me-1'></i>
                  QR Code
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Coluna de Ações */}
        <div className='col-md-4'>
          <div className='card'>
            <div className='card-header'>
              <h3 className='card-title'>Ações</h3>
            </div>
            <div className='card-body d-flex flex-column gap-3'>
              <button
                className='btn btn-primary'
                onClick={handleSave}
                disabled={saving}
                style={{ height: '45px', fontSize: '16px', fontWeight: '500' }}
              >
                {saving ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className='fas fa-save me-2'></i>
                    Salvar Configurações
                  </>
                )}
              </button>

              <button
                className='btn btn-secondary'
                onClick={loadConfig}
                disabled={saving || loading}
                style={{ height: '45px', fontSize: '16px', fontWeight: '500' }}
              >
                <i className='fas fa-sync-alt me-2'></i>
                Recarregar
              </button>

              <button
                className='btn btn-info'
                onClick={() => setShowQRModal(true)}
                disabled={saving || !effectivePanelLink}
                style={{ height: '45px', fontSize: '16px', fontWeight: '500' }}
              >
                <i className='fas fa-qrcode me-2'></i>
                Gerar QR Code
              </button>

              <button
                className='btn btn-danger'
                onClick={handleDelete}
                disabled={saving || loading || !hasExistingConfig}
                style={{ height: '45px', fontSize: '16px', fontWeight: '500' }}
              >
                <i className='fas fa-trash me-2'></i>
                Deletar Tudo
              </button>

              <div className='alert alert-info mt-3' role='alert'>
                <i className='fas fa-info-circle me-2'></i>
                <strong>Status:</strong> <br />
                {hasExistingConfig ? (
                  <span className='text-success'>Configurações ativas</span>
                ) : (
                  <span className='text-warning'>Nenhuma configuração salva</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <PrintQRCodeModal
        show={showQRModal}
        onHide={() => setShowQRModal(false)}
        linkPainel={effectivePanelLink}
        title='Imprimir QR Code da Fila'
      />
    </div>
  )
}
